import React from "react"
import PubSub from "pubsub-js"
import { PaymentElement } from "@stripe/react-stripe-js"
import usePaymentSubmission from "./use-payment-submission"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Value from "aldoo-ra/Value"

const { useAuth } = PlatformAuth

// A project may hold the pay button until something on the payment step is
// done (a required consent in the checkout-form-header slot): setValue of
// this key to a reason holds it, null releases it.
export const CHECKOUT_HOLD_KEY = "aldoo-checkout-hold"

// Another way to pay chosen on the payment step (a project's choice in the
// checkout-form-header slot — cash on delivery): setValue of this key to
// { id, continueText, submit: async () => {} } hides the card form and has the
// button do `submit` under that label; null brings the card back.
export const CHECKOUT_ALTERNATIVE_KEY = "aldoo-checkout-alternative"
const CheckoutForm = ({ store, isActive }) => {
  const { handleSubmit, ready, setReady, isReady, loading } = usePaymentSubmission(store)
  const [auth] = useAuth()
  const [hold] = Value(CHECKOUT_HOLD_KEY, null)
  const [alternative] = Value(CHECKOUT_ALTERNATIVE_KEY, null)
  const [altBusy, setAltBusy] = React.useState(false)
  const CheckoutPreparingPayment = store.getFragment("checkout-preparing-payment")
  // Stripe could not load the form: its own error shows in place, not the loader over it
  const [loadFailed, setLoadFailed] = React.useState(false)
  // the step's own label, put back when the card is chosen again after another way
  const [continueText] = Value("purchase-flow-continue-text", null)
  const cardText = React.useRef(null)
  if (!alternative?.submit && continueText) cardText.current = continueText

  const { hideCheckout, showCart, getFragment, purchaseUserEmail, tempAuthForPurchase, purchaseFlow } = store

  const CheckoutTotalBar = getFragment("checkout-total-bar")
  // Optional project slot above the payment element (order summary…)
  const CheckoutFormHeader = getFragment("checkout-form-header")

  // Get email from auth, tempAuth, or purchaseUserEmail (in order of preference)
  const userEmail = auth?.email || tempAuthForPurchase?.email || purchaseUserEmail || ''

  // A flow may tune the Payment Element (fields collected, layout…):
  //   paymentElementOptions: (context) => ({ fields: { billingDetails: { address: "never" } } })
  const flowOptions = purchaseFlow?.flowDefinition?.paymentElementOptions
  const extraOptions = (typeof flowOptions === 'function' ? flowOptions({ store }) : flowOptions) || {}

  const handleBackToInvoice = () => {
    PubSub.publish("PURCHASE_FLOW_BACK")
  }

  const handlePaymentSubmit = async () => {
    if (hold) return
    // Email is already set up in handleEmailVerification (before checkout)
    // Complete payment with stored invoice data
    await handleSubmit(store.invoiceData, userEmail)
  }

  const handleAlternativeSubmit = async () => {
    if (hold || altBusy || !alternative?.submit) return
    setAltBusy(true)
    try {
      await alternative.submit()
    } finally {
      setAltBusy(false)
    }
  }

  // Update button state via PubSub - only when this step is active
  React.useEffect(() => {
    if (!isActive) {
      return
    }

    // another way to pay chosen: its own label and action, the card form not waited for
    if (alternative?.submit) {
      PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
        stepId: 'payment',
        onContinue: handleAlternativeSubmit,
        onBack: handleBackToInvoice,
        canContinue: !altBusy && !hold,
        loading: altBusy,
        ...(alternative.continueText ? { continueText: alternative.continueText } : {}),
      })
      return
    }

    PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
      stepId: 'payment',
      onContinue: handlePaymentSubmit,
      onBack: handleBackToInvoice,
      canContinue: isReady && !loading && !hold,
      loading: loading,
      ...(cardText.current ? { continueText: cardText.current } : {}),
    })
  }, [isReady, loading, isActive, hold, alternative, altBusy])

  return (
    <div className="relative h-full flex flex-col justify-between">
      {/* the card form loading: one loader in the middle of the step, over Stripe's own skeleton */}
      {!ready && !loadFailed && !alternative?.submit && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-paper" data-testid="checkout-payment-loading">
          <CheckoutPreparingPayment />
        </div>
      )}
      <div className="pt-8 pb-[70px] px-2 md:px-6">
        {CheckoutFormHeader && <CheckoutFormHeader store={store} />}
        {/* kept mounted under another way to pay, so choosing the card again finds it ready */}
        <div className={`mb-4 ${alternative?.submit ? "hidden" : ""}`} data-testid="checkout-payment-form" data-hold={hold || undefined}>
          <PaymentElement
            onReady={() => setReady(true)}
            onLoadError={() => setLoadFailed(true)}
            options={{
              defaultValues: {
                billingDetails: {
                  email: userEmail
                }
              },
              ...extraOptions,
            }}
          />
        </div>
      </div>

      {/* Invoice link + total - Sticky Bottom */}
      <CheckoutTotalBar store={store} />
    </div>
  )
}

export default CheckoutForm
